import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useProperty } from '../../context/PropertyContext';

const ProjectList = ({ projects, onEdit, onDelete }) => {
  const { formatPrice } = useProperty();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');

  // Filter projects based on search and filters
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || project.status === statusFilter;
    const matchesType = typeFilter === 'All' || project.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusClass = (status) => {
    switch (status) {
      case 'Completed':
        return 'completed';
      case 'Ongoing':
        return 'ongoing';
      case 'Planning':
        return 'planning';
      case 'On Hold':
        return 'on-hold';
      default:
        return 'planning';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUniqueStatuses = () => {
    const statuses = [...new Set(projects.map(p => p.status))];
    return ['All', ...statuses];
  };

  const getUniqueTypes = () => {
    const types = [...new Set(projects.map(p => p.type))];
    return ['All', ...types];
  };

  if (projects.length === 0) {
    return (
      <div className="property-list">
        <div className="empty-state">
          <i className="fas fa-building"></i>
          <h3>No Projects Found</h3>
          <p>Start by adding your first project to showcase your portfolio.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="property-list">
      <div className="list-controls">
        <div className="search-bar">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {getUniqueStatuses().map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {getUniqueTypes().map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="projects-table">
        <div className="table-header">
          <div>Image</div>
          <div>Project Details</div>
          <div>Timeline</div>
          <div>Value</div>
          <div>Status</div>
          <div>Actions</div>
        </div>

        <div className="table-body">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              className="table-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="col-image">
                <img
                  src={project.image}
                  alt={project.title}
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&h=600&fit=crop&crop=center';
                  }}
                />
              </div>

              <div className="col-details">
                <h4>{project.title}</h4>
                <div className="location">
                  <i className="fas fa-map-marker-alt"></i>
                  {project.location}
                </div>
                <div className="project-features">
                  <span>
                    <i className="fas fa-building"></i>
                    {project.type}
                  </span>
                  <span>
                    <i className="fas fa-home"></i>
                    {project.units}
                  </span>
                </div>
                <div className="project-type">{project.type}</div>
              </div>

              <div className="col-timeline">
                <div className="timeline-item">
                  <strong>Start:</strong> {formatDate(project.startDate)}
                </div>
                <div className="timeline-item">
                  <strong>End:</strong> {formatDate(project.completionDate)}
                </div>
                {project.startDate && project.completionDate && (
                  <div className="timeline-duration">
                    <i className="fas fa-clock"></i>
                    {Math.ceil((new Date(project.completionDate) - new Date(project.startDate)) / (1000 * 60 * 60 * 24 * 30))} months
                  </div>
                )}
              </div>

              <div className="col-price">
                <div className="price">
                  {formatPrice(project.totalValue || 0)}
                </div>
              </div>

              <div className="col-status">
                <span className={`status ${getStatusClass(project.status)}`}>
                  {project.status}
                </span>
              </div>

              <div className="col-actions">
                <button
                  className="action-btn edit"
                  onClick={() => onEdit(project)}
                  title="Edit Project"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button
                  className="action-btn delete"
                  onClick={() => onDelete(project.id)}
                  title="Delete Project"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {filteredProjects.length === 0 && searchTerm && (
        <div className="empty-state">
          <i className="fas fa-search"></i>
          <h3>No Projects Found</h3>
          <p>No projects match your search criteria. Try adjusting your filters.</p>
        </div>
      )}
    </div>
  );
};

export default ProjectList;